import * as Sentry from "@sentry/react";

// Configuration Sentry pour DooDates
// `appName` identifie le produit (date-polls, form-polls, …) dans le tag Sentry
// `app` afin de filtrer/router les erreurs par app dans le dashboard.
export function initSentry(appName: string = "doodates") {
  // Ne pas initialiser en développement sauf si explicitement demandé
  if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_DSN) {
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment =
    import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;

  if (!dsn) {
    console.warn("Sentry DSN not configured, skipping initialization");
    return;
  }

  Sentry.init({
    dsn,
    environment,

    // Sampling rates
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% en production, 100% en dev

    // Filtrage des erreurs
    beforeSend(event) {
      // Ignorer les erreurs de développement
      if (import.meta.env.DEV) {
        return null;
      }

      // Ignorer les erreurs communes non critiques
      const ignoredErrors = [
        /Network Error/i,
        /Request aborted/i,
        /ChunkLoadError/i,
        /Loading CSS chunk/i,
        /Non-Error promise rejection captured/i,
        /signal is aborted/i,
        /the operation was aborted/i,
        /AbortError/i,
      ];

      if (event.exception) {
        const exc = event.exception.values?.[0];
        const errorMessage = exc?.value;
        const errorType = exc?.type;
        if (errorType === "AbortError") {
          return null;
        }
        if (ignoredErrors.some((pattern) => pattern.test(errorMessage || ""))) {
          return null;
        }

        // Bruit des navigateurs intégrés (Instagram/Facebook in-app browser) :
        // leur script de perf logging injecté (iabjs://navigation_performance_logger_*)
        // appelle son pont natif Java/Kotlin via postMessage, qui peut avoir été
        // détruit entre-temps (navigation, mise en arrière-plan) — DOODATES-A.
        // Détecté par : iabjs:// dans frames OU culprit OU message d'erreur.
        const frames = exc?.stacktrace?.frames ?? [];
        const culprit = ((event as any).culprit || "") as string;
        const hasIabjsFrame =
          /^iabjs:\/\//i.test(culprit) ||
          frames.some((f) => /^iabjs:\/\//i.test(f.filename || ""));
        const hasOwnAppFrame = frames.some((f) =>
          /\/assets\/.+\.js/.test(f.filename || ""),
        );
        // Ignorer aussi si "Java object is gone" (erreur postMessage WebView)
        const isWebViewNativeError =
          /Java object is gone|invoking (postMessage|.*Message)/i.test(
            errorMessage || "",
          );
        if ((hasIabjsFrame && !hasOwnAppFrame) || isWebViewNativeError) {
          return null;
        }

        // Bruit d'extensions navigateur : EvalError CSP dont la stack ne contient
        // que des frames <anonymous> (pas de fichier /assets/*.js applicatif).
        // Les extensions Chrome exécutent leur code dans le contexte de la page
        // et héritent donc de notre CSP — leurs new Function/eval échouent et
        // remontent ici comme si c'était notre code.
        if (
          errorType === "EvalError" &&
          /unsafe-eval/i.test(errorMessage || "")
        ) {
          const frames = exc?.stacktrace?.frames ?? [];
          // Frames Sentry : du plus ancien au plus récent. Le throw vient du
          // dernier frame ; si celui-ci n'est pas notre bundle, l'eval est
          // d'une extension, même si un frame applicatif apparaît plus haut
          // dans la stack (promesse rejetée relayée par notre code).
          const innermost = frames[frames.length - 1];
          const innermostIsApp = /\/assets\/.+\.js/.test(
            innermost?.filename || "",
          );
          if (!innermostIsApp) {
            return null;
          }
        }
      }

      // Nettoyer les données sensibles
      if (event.request?.cookies) {
        delete event.request.cookies;
      }

      if (event.user?.email) {
        event.user.email = event.user.email.replace(/(.{2}).*@/, "$1***@");
      }

      return event;
    },

    // Contexte utilisateur
    initialScope: {
      tags: {
        app: appName,
        version: import.meta.env.VITE_APP_VERSION || "1.0.0",
      },
    },

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || "1.0.0",
  });

  console.info("Sentry initialized for environment:", environment);
}

// Helper pour capturer les erreurs manuellement
export function captureError(error: Error, context?: Record<string, any>) {
  if (Sentry) {
    Sentry.captureException(error, {
      extra: context,
    });
  }
}

// Helper pour capturer les messages
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info",
) {
  if (Sentry) {
    Sentry.captureMessage(message, level);
  }
}

// Helper pour définir le contexte utilisateur
export function setUserContext(user: {
  id?: string;
  email?: string;
  name?: string;
}) {
  if (Sentry) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name,
    });
  }
}

// Helper pour ajouter du contexte
export function setContext(key: string, context: Record<string, any>) {
  if (Sentry) {
    Sentry.setContext(key, context);
  }
}

// Helper pour ajouter des breadcrumbs
export function addBreadcrumb(crumb: Sentry.Breadcrumb) {
  if (Sentry) {
    Sentry.addBreadcrumb(crumb);
  }
}
